import { computed, ref } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import store from '@/state/store';

// Zentraler Reseller-Daten-Cache: Wiederverkaufs-Status (GET /my/settings) und
// End-Kunden-Liste (GET /my/reseller-end-customers) — einmal geladen, per Polling
// aktuell gehalten und nach Mutationen sofort neu geladen.
// Nur für Nicht-Admins mit VM- + Finanz-Zugriff relevant.
export const resaleActive = ref(false);
export const resaleLoaded = ref(false);
export const resellerEndCustomers = ref([]);
export const resellerEndCustomersLoaded = ref(false);

// Kennungen lowercase — Abgleich mit Verzeichnis-Prefixen ist case-insensitive.
export const resellerEndCustomerCodes = computed(() => new Set(resellerEndCustomers.value
  .map((customer) => String(customer?.code || '').trim().toLowerCase())
  .filter(Boolean)));

let pending = null;
let endCustomersPending = null;
let pollTimer = null;
const POLL_MS = 60000;

const eligible = () => {
  const g = store.getters;
  return !g['auth/canAccessAdmin'] && Boolean(g['auth/canAccessFinancial']) && Boolean(g['auth/canAccessVm']);
};

export const loadResaleStatus = ({ force = false } = {}) => {
  if (!eligible()) {
    resaleActive.value = false;
    resaleLoaded.value = true;
    return Promise.resolve(false);
  }
  if (pending) return pending;
  if (resaleLoaded.value && !force) return Promise.resolve(resaleActive.value);
  // skipAuthLogout: ein 403 (z.B. keine Kundennummer in der Session) darf nicht ausloggen.
  pending = axios.get(`${API_BASE_URL}/my/settings`, { withCredentials: true, skipAuthLogout: true })
    .then((response) => {
      resaleActive.value = Boolean(response.data?.resale);
      resaleLoaded.value = true;
      return resaleActive.value;
    })
    .catch(() => {
      resaleLoaded.value = true;
      return resaleActive.value;
    })
    .finally(() => { pending = null; });
  return pending;
};

export const loadResellerEndCustomers = ({ force = false } = {}) => {
  if (!eligible() || !resaleActive.value) {
    resellerEndCustomers.value = [];
    resellerEndCustomersLoaded.value = true;
    return Promise.resolve(resellerEndCustomers.value);
  }
  if (endCustomersPending) return endCustomersPending;
  if (resellerEndCustomersLoaded.value && !force) return Promise.resolve(resellerEndCustomers.value);
  endCustomersPending = axios.get(`${API_BASE_URL}/my/reseller-end-customers`, { withCredentials: true, skipAuthLogout: true })
    .then((response) => {
      resellerEndCustomers.value = Array.isArray(response.data?.endCustomers) ? response.data.endCustomers : [];
      resellerEndCustomersLoaded.value = true;
      return resellerEndCustomers.value;
    })
    .catch(() => resellerEndCustomers.value)
    .finally(() => { endCustomersPending = null; });
  return endCustomersPending;
};

// Beides frisch laden (Settings zuerst — steuert, ob End-Kunden relevant sind).
export const refreshResellerData = async ({ force = true } = {}) => {
  const active = await loadResaleStatus({ force });
  await loadResellerEndCustomers({ force });
  return active;
};

// Einmal starten (idempotent): initial laden + jede Minute aktualisieren.
// Für Admins ist jeder Tick ein No-op (eligible-Guard); versteckte Tabs pausieren.
export const startResellerDataPolling = () => {
  loadResaleStatus().then(() => loadResellerEndCustomers()).catch(() => {});
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    refreshResellerData({ force: true }).catch(() => {});
  }, POLL_MS);
};

// Nach Mutationen (Einstellungen gespeichert, End-Kunden editiert) sofort neu laden.
export const notifyResellerDataChanged = () => refreshResellerData({ force: true }).catch(() => {});

// Nach PUT /my/settings direkt aktualisieren (Menüpunkte reagieren sofort);
// End-Kunden-Cache dem neuen Status entsprechend nachziehen.
export const setResaleActive = (value) => {
  resaleActive.value = Boolean(value);
  resaleLoaded.value = true;
  loadResellerEndCustomers({ force: true }).catch(() => {});
};
